import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { categorizeFeedback } from 'utlis/categorizeFeedback';
import { DEMO_VIDEO, TEMPLATE_TITLES } from 'utlis/constants';
import { prefetchImagesAsBlobs } from 'utlis/image';

import { FeedbackList } from './FeedbackList';
import ImageCarousel from './ImageCarousel';

import styles from './styles.module.scss';

const CategoryDetailView = ({
  category,
  onBackClick,
  isPublicView = false,
  onCategoryClick,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedFeedbackIndex, setSelectedFeedbackIndex] = useState(null);
  const [imagesLoading, setImagesLoading] = useState(false);
  const userClickSupersedesScrollRef = useRef(false);

  const reviewType = currentRecord?.extra_data?.template ?? currentRecord?.template;

  const [prefetchedImages, setPrefetchedImages] = useState([]);

  const categorizedFeedback = useMemo(
    () => {
      const result = categorizeFeedback(currentRecord?.final_feedback || [], reviewType, currentRecord);

      return result;
    },
    [currentRecord, reviewType]
  );

  const selectedCategoryFeedback = useMemo(
    () => feedbackToSend(),
    [feedbackToSend, currentRecord, categorizedFeedback, category]
  );

  // Transform design images to proper format using current record's frame_images
  const rawImageUrls = useMemo(() => {
    if (!currentRecord?.extra_data?.frame_images || !Array.isArray(currentRecord.extra_data.frame_images)) {
      return [];
    }

    const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
    const postfixUrl = '?alt=media';

    return currentRecord.extra_data.frame_images.map(url => `${prefixUrl}${encodeURIComponent(url)}${postfixUrl}`);
  }, [currentRecord?.extra_data?.frame_images]);

  // Prefetch all images when rawImageUrls change
  useEffect(() => {
    if (rawImageUrls.length === 0) {
      setPrefetchedImages([]);
      setImagesLoading(false);

      return;
    }

    const prefetchImages = async () => {
      setImagesLoading(true);

      try {
        const prefetched = await prefetchImagesAsBlobs(rawImageUrls);

        setPrefetchedImages(prefetched);
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to prefetch images:', error);

        // Fallback to original URLs
        const fallbackImages = rawImageUrls.map((url, index) => ({
          originalUrl: url,
          blobUrl: url,
          id: index,
          page: index + 1,
          alt: `Design image ${index + 1}`,
        }));

        setPrefetchedImages(fallbackImages);
      }
      finally {
        setImagesLoading(false);
      }
    };

    prefetchImages();
  }, [rawImageUrls]);

  // Transform prefetched images to the format expected by ImageCarousel
  const formattedImages = useMemo(() => prefetchedImages.map((image, index) => ({
    id: index,
    url: image.blobUrl, // Use the prefetched blob URL instead of original URL
    page: index + 1, // Assuming 1-indexed pages
    alt: `Design image ${index + 1}`,
  })), [prefetchedImages]);

  // When category changes, jump to the screen that contains the first feedback item
  useEffect(() => {
    setSelectedFeedbackIndex(null);
    userClickSupersedesScrollRef.current = false;

    const firstFeedbackWithPage = selectedCategoryFeedback?.find(item => item?.page);

    setSelectedImageIndex(firstFeedbackWithPage ? firstFeedbackWithPage.page - 1 : 0);
  }, [category]);

  const handleImageIndexChange = useCallback(
    index => {
      userClickSupersedesScrollRef.current = true;
      setSelectedImageIndex(index);
    },
    []
  );

  const handlePinClick = useCallback(
    feedbackIndex => {
      userClickSupersedesScrollRef.current = true;

      if (typeof feedbackIndex === 'number' && feedbackIndex >= 0) {
        setSelectedFeedbackIndex(feedbackIndex);
      }
    },
    []
  );

  return (
    <>
      {/* Image Carousel Section */}
      <div className={cn('flex-[3] mx-8', styles['detail-preview'], styles.createReviewLeftPanel)}>
        <ImageCarousel
          feedback={selectedCategoryFeedback}
          images={formattedImages}
          onBackClick={onBackClick}
          selectedImageIndex={selectedImageIndex}
          onImageIndexChange={handleImageIndexChange}
          onPinClick={handlePinClick}
          isLoading={imagesLoading}
        />
      </div>

      <FeedbackList
        feedback={selectedCategoryFeedback}
        categoryName={getCategoryName()}
        watchDemo={watchDemo}
        isPublicView={isPublicView}
        onCategoryClick={onCategoryClick}
        onInspectFeedback={handleInspectFeedback}
        totalImages={formattedImages.length}
        userClickSupersedesScrollRef={userClickSupersedesScrollRef}
        selectedFeedbackIndex={selectedFeedbackIndex}
      />
    </>
  );

  function watchDemo() {
    window.open(DEMO_VIDEO.URL, '_blank');
  }

  function feedbackToSend() {
    if (currentRecord?.extra_data?.template === TEMPLATE_TITLES.EDGE_CASES) {
      return currentRecord?.final_feedback?.map((item, index) => ({
        ...item,
        id: item.id || index,
        index,
      }));
    }

    // Try to find by categoryKey first, then by categoryName
    const categoryData = categorizedFeedback.find(
      ({ categoryKey, categoryName }) => categoryKey === category || categoryName === category
    );

    // Ensure each feedback item has an ID
    const feedbackItems = categoryData?.categoryFeedback || [];
    const processedItems = feedbackItems.map((item, index) => ({
      ...item,
      id: item.id || index,
    }));

    return processedItems;
  }

  function handleInspectFeedback(feedbackData) {
    if (!feedbackData.page) {
      return;
    }

    userClickSupersedesScrollRef.current = true;

    const cardIndex = selectedCategoryFeedback.findIndex(item => item === feedbackData);

    if (cardIndex >= 0) {
      setSelectedFeedbackIndex(cardIndex);
    }

    // Convert page number to 0-based index for the carousel
    const imageIndex = feedbackData.page - 1;

    // Validate that the page is within the valid range of images
    if (imageIndex < 0 || imageIndex >= formattedImages.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `Cannot navigate to page ${feedbackData.page}: out of range (available images: ${formattedImages.length})`
      );

      return;
    }

    // Directly update the selected image index
    setSelectedImageIndex(imageIndex);
  }

  function getCategoryName() {
    if (currentRecord?.extra_data?.template === TEMPLATE_TITLES.EDGE_CASES) {
      return currentRecord.extra_data?.review_frame_name || 'Review Frame';
    }

    const categoryData = categorizedFeedback.find(
      ({ categoryKey }) => categoryKey === category
    );

    return categoryData?.categoryName || currentRecord?.extra_data?.review_frame_name;
  }
};

CategoryDetailView.propTypes = {
  category: PropTypes.string.isRequired,
  onBackClick: PropTypes.func.isRequired,
  isPublicView: PropTypes.bool,
  onCategoryClick: PropTypes.func.isRequired,
};

CategoryDetailView.defaultProps = {
  feedbackData: [],
  className: '',
};

export default CategoryDetailView;
